import { RequestMessageHeader } from "@westpierstudio/core-module-oryx/services/comms/types/BaseTypes";
import { SpinGamePlayData } from "./gameevents/InitialSpin";
import { WheelGamePlayData } from "./gameevents/Wheel";
import { FreeSpinGamePlayData } from "./gameevents/FreeSpin";
import { FreeSpinWheelGamePlayData } from "./gameevents/FreeSpinWheel";
import { GameEvent, GameRequest } from "./ServerEvents";
import { FREE_ROUND } from "./gameevents/OpenGame";

type GamePlayData = SpinGamePlayData | WheelGamePlayData | FreeSpinGamePlayData | FreeSpinWheelGamePlayData;
export type LastEventData = Required<Omit<BaseGameEventData<GamePlayData>, "roundId">>;

type LastEvent = {
    correlationSeqId: number,
    data: LastEventData,
    event: GameEvent,
    seqId: number
}

export type NetPosition = {
    bets: number,
    netProfit: number,
    wins: number
}

export type BaseGameEvent<TEvent, TData> = {
    header: RequestMessageHeader,
    body: {
        balance: number,
        balanceVersion: number,
        correlationSeqId: number,
        data: TData,
        elapsedTimeInMillis?: number
        event: TEvent,
        lastEvent?: LastEvent,
        netPosition?: NetPosition,
        lastRoundId?: string,
        seqId: number
    }
}

export type BaseGameEventData<TGamePlayData> = {
    accumulatedCoinsWon: number,
    accumulatedMoneyWon: number,
    balanceAfterBetData: {
        balanceAfterBet: number,
        balanceAfterBetVersion: number
    },
    betData: BetData,
    buyInId: number,
    finalPersistingState: {
        multiplierCountersMap: MultiplierCountersMap
    },
    gameplay: TGamePlayData,
    isRoundFinished: boolean,
    nextActions?: GameRequest[]
    roundId: string,
    totalCoinsWon: number,
    totalMoneyWon: number,
    freeRounds?: FREE_ROUND
}

export type BetData = {
    coinSize: number,
    coinsPerBet: number,
    playerBet: number
}

export type CoreSymbolID = "M1" | "M2" | "M3" | "F4" | "F5" | "F6";
export type SymbolID = CoreSymbolID | "B1" | "WW" | "B1W";

export enum CashWedgeID {
    CASH_0 = `CASH_0`,
    CASH_1 = `CASH_1`,
    CASH_2 = `CASH_2`,
    CASH_3 = `CASH_3`,
    CASH_4 = `CASH_4`,
    CASH_5 = `CASH_5`
}

export function IsCacheWedgeID(id: WedgeID): id is CashWedgeID {
    return [CashWedgeID.CASH_0, CashWedgeID.CASH_1, CashWedgeID.CASH_2, CashWedgeID.CASH_3, CashWedgeID.CASH_4, CashWedgeID.CASH_5].includes(id as CashWedgeID);
}

export function IsJackpotWedgeID(id: WedgeID): id is JackpotWedgeID {
    return [JackpotWedgeID.JACKPOT_0, JackpotWedgeID.JACKPOT_1, JackpotWedgeID.JACKPOT_2].includes(id as JackpotWedgeID);
}

export function IsFreeSpinsWedgeID(id: WedgeID): id is FreeSpinWedgeID {
    return [FreeSpinWedgeID.FREE_SPINS_0, FreeSpinWedgeID.FREE_SPINS_1, FreeSpinWedgeID.FREE_SPINS_2].includes(id as FreeSpinWedgeID);
}

export enum FreeSpinWedgeID {
    FREE_SPINS_0 = `FREE_SPINS_0`,
    FREE_SPINS_1 = `FREE_SPINS_1`,
    FREE_SPINS_2 = `FREE_SPINS_2`
}

export enum JackpotWedgeID {
    JACKPOT_0 = `JACKPOT_0`,
    JACKPOT_1 = `JACKPOT_1`,
    JACKPOT_2 = `JACKPOT_2`
}

export enum MultiplierWedgeID {
    MULTIPLIERS_0 = `MULTIPLIERS_0`,
    MULTIPLIERS_1 = `MULTIPLIERS_1`,
    MULTIPLIERS_2 = `MULTIPLIERS_2`,
    MULTIPLIERS_3 = `MULTIPLIERS_3`
}

export type WedgeID = CashWedgeID | FreeSpinWedgeID | JackpotWedgeID | MultiplierWedgeID;

export type ReelSymbols = [SymbolID, SymbolID, SymbolID];

export type ReelSetSymbols = [
    ReelSymbols,
    ReelSymbols,
    ReelSymbols,
    ReelSymbols,
    ReelSymbols
];

export type CoreSymbolNumberMap = {[symbolID in CoreSymbolID]: number};
export type MultiplierCountersMap = {[coinSize: number]: Partial<CoreSymbolNumberMap>};
export type MultipliedWinsMap = {[paylineWinArrayIndex: number]: {multiplier: number, multipliedCoinsWon: number, multipliedMoneyWon: number}}

export type PayLineWin = {
    coinsWon: number,
    moneyWon: number,
    oakIndex: number,
    pattern: {column: number, row: number}[],
    paylineIndex: number,
    symbol: CoreSymbolID
}

export type Multipliers = {
    finalCounters: Partial<CoreSymbolNumberMap>,
    initialCounters: Partial<CoreSymbolNumberMap>,
    newSymbols: CoreSymbolID[],
    removedSymbols: CoreSymbolID[]
}

const MultiplierPanelSymbolMap = {
    "M1": 0,
    "M2": 1,
    "M3": 2,
    "F4": 3,
    "F5": 4,
    "F6": 5
} as const;

export function getMultiplierPanelIndex(symbol: CoreSymbolID) {
    return MultiplierPanelSymbolMap[symbol];
}

export type FinalFreeSpinsCounter = {
    freeSpinsRemaining: number,
    freeSpinsTotal: number
}

export type OrderedWedgeData = {
    id: WedgeID,
    coins: number,
    freeSpins?: number,
    symbols?: CoreSymbolID[]
}[];

export type TriggeringState = {
    bonusSymbolPositions: {column: number, row: number}[],
    multipliedWinsMap: MultipliedWinsMap,
    paylineWins: PayLineWin[],
    reels: ReelSetSymbols
}